import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { cn } from '@/lib/utils';

import type { ValidatableInput } from './InputInterface';
import { useTranslation } from '@/hooks/useTranslation';

type RequiredMultiGroupProps = {
  children: React.ReactNode;
  minRequired: number;
  message?: string;
  inputRefs: React.RefObject<ValidatableInput | null>[];
  className?: string;
};

export const RequiredMultiGroup = forwardRef(function RequiredMultiGroup(
  { children, minRequired, message, inputRefs, className }: RequiredMultiGroupProps,
  ref: React.Ref<{ validate: () => boolean }>
) {
  const groupRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('form.validation.requiredMultiAtLeastOne');

  function validateSingle(): boolean {
    for (const inputRef of inputRefs) {
      if (inputRef.current && !inputRef.current.validate()) {
        return false;
      }
    }
    return true;
  }

  function validateMulti(): boolean {
    let filledCount: number = 0;

    inputRefs.forEach((inputRef) => {
      const inputEl = inputRef.current;
      if (!inputEl || !inputEl.inputRef?.current) return;

      const htmlInput = inputEl.inputRef.current;
      if (htmlInput.value !== '' && htmlInput.value !== '0') {
        filledCount++;
      }
    });
    return filledCount >= minRequired;
  }

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!validateSingle()) return false;
      if (!validateMulti()) {
        const htmlInput = inputRefs[0].current?.inputRef;
        htmlInput?.current.setCustomValidity(resolvedMessage);
        htmlInput?.current.reportValidity();
        htmlInput?.current.setCustomValidity(''); // reset après affichage

        return false;
      }
      return true;
    },
  }));

  return (
    <div className={cn('contents', className)} ref={groupRef}>
      {children}
    </div>
  );
});
